import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, Package, BookOpen, ChevronRight } from "lucide-react";
import { companyInfo as mockCompany, products as mockProducts, galleryImages } from "../data/mock";
import { useProducts, useCompany } from "../hooks/useContent";
import PageHero from "../components/PageHero";

export default function ProductsPage() {
  const { data: products } = useProducts();
  const companyInfo = mockCompany;
  return (
    <div>
      <Helmet>
        <title>Produse Funerare – Sicrie, Coroane & Accesorii | Casa Funerară Sfântul Andrei</title>
        <meta name="description" content="Produse funerare de calitate: sicrie, coroane și aranjamente florale, îmbrăcăminte și accesorii. Catalog complet disponibil la Casa Funerară Sfântul Andrei Constanța." />
        <link rel="canonical" href="https://funeraresfantulandrei.ro/produse" />
        <meta property="og:title" content="Produse Funerare – Casa Funerară Sfântul Andrei Constanța" />
        <meta property="og:url" content="https://funeraresfantulandrei.ro/produse" />
      </Helmet>
      <PageHero
        image={galleryImages.sfAndreiVitraliu}
        title="Produse Funerare"
        subtitle="Dispunem de stoc permanent și un catalog complet de produse funerare. Toate produsele sunt de calitate superioară, selectate cu grijă."
        breadcrumbs={[
          { label: "Acasă", to: "/" },
          { label: "Produse" },
        ]}
      />

      {/* Coffins Section */}
      <section className="section-spacing bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <span className="text-xs uppercase tracking-[4px] text-[#999999] font-medium">
                Sicrie
              </span>
              <h2
                className="text-2xl sm:text-3xl font-semibold mt-4 mb-8"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Tipuri de Sicrie
              </h2>
              <div className="space-y-6">
                {products.sicrie.map((item, i) => (
                  <div key={i} className="product-card group">
                    <h3
                      className="text-lg font-semibold mb-2 text-[#0f0f0f]"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {item.category}
                    </h3>
                    <p className="text-sm text-[#999999] leading-relaxed mb-3">{item.description}</p>
                    <span className="text-xs uppercase tracking-wider text-[#0f0f0f] font-semibold">
                      {item.priceRange}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:sticky lg:top-36">
              <div className="img-hover-zoom overflow-hidden aspect-[4/3]">
                <img
                  src={galleryImages.showroomDetail}
                  alt="Showroom sicrie și coroane funerare – Casa Funerară Sfântul Andrei Constanța"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="text-xs text-[#999999] mt-3 text-center italic">
                Showroom-ul nostru cu sicrie și coroane funerare
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Flower Arrangements Gallery */}
      <section className="py-16 bg-[#FAFAF8] border-y border-[#E8E4DC]">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="mb-10">
            <span className="text-xs uppercase tracking-[4px] text-[#999999] font-medium">
              Aranjamente Florale
            </span>
            <h2
              className="text-2xl sm:text-3xl font-semibold mt-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Coroane și Aranjamente Florale
            </h2>
            <p className="text-sm text-[#999999] mt-3 max-w-xl">
              Dispunem de o varietate de coroane și aranjamente florale funerare, 
              realizate cu atenție și rafinament.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="img-hover-zoom overflow-hidden aspect-[4/3]">
              <img
                src={galleryImages.showroomCoffinsWreaths}
                alt="Sicrie și coroane funerare colorate – Casa Funerară Sfântul Andrei"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="img-hover-zoom overflow-hidden aspect-[4/3]">
              <img
                src={galleryImages.showroomDetail}
                alt="Sicriu deschis cu coroane funerare – showroom Casa Funerară Sfântul Andrei"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="img-hover-zoom overflow-hidden aspect-[4/3]">
              <img
                src={galleryImages.flowers1}
                alt="Coroane funerare cu flori albe și albastre – Casa Funerară Sfântul Andrei"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="img-hover-zoom overflow-hidden aspect-[4/3]">
              <img
                src={galleryImages.flowers2}
                alt="Aranjamente florale funerare colorate – coroane și buchete tradiționale"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="img-hover-zoom overflow-hidden aspect-[4/3]">
              <img
                src={galleryImages.tricolorWreath}
                alt="Coroană funerară tricolor – Glorie Eternă Eroilor Neamului"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="img-hover-zoom overflow-hidden aspect-[4/3]">
              <img
                src={galleryImages.waxCandle}
                alt="Lumânare tradițională din ceară cu cruce – accesorii funerare"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Other Products */}
      <section className="section-spacing bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="mb-10">
                <span className="text-xs uppercase tracking-[4px] text-[#999999] font-medium">
                  Accesorii & Pachete
                </span>
                <h2
                  className="text-2xl sm:text-3xl font-semibold mt-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Produse Funerare și Tradiționale
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {products.produse.map((item, i) => (
                  <div key={i} className="p-5 border border-[#E8E4DC] hover:border-[#cccccc] transition-all duration-400 group">
                    <h3 className="text-base font-semibold text-[#0f0f0f] mb-2">{item.name}</h3>
                    <p className="text-sm text-[#999999] leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:sticky lg:top-36 space-y-6">
              <div className="img-hover-zoom overflow-hidden aspect-[4/3]">
                <img
                  src={galleryImages.coliva}
                  alt="Colivă tradițională de pomenire – Casa Funerară Sfântul Andrei"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="img-hover-zoom overflow-hidden aspect-[4/3]">
                <img
                  src={galleryImages.parastasPackages}
                  alt="Pachete tradiționale de parastas pregătite în biserică"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="text-xs text-[#999999] text-center italic">
                Colivă tradițională și pachete de parastas pregătite cu grijă
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Îmbrăcăminte Decedați */}
      <section className="section-spacing" style={{ backgroundColor: "#FAFAF8" }}>
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs uppercase tracking-[4px] text-[#C6A85A] font-medium">
              Îmbrăcăminte
            </span>
            <h2
              className="text-2xl sm:text-3xl font-semibold mt-4 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Îmbrăcăminte Decedați
            </h2>
            <p className="text-sm text-[#666666] leading-relaxed">
              Oferim o gamă completă de îmbrăcăminte pentru pregătirea decedaților –
              costume, rochii, pantofi și accesorii, toate selectate cu grijă și respect.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-[#E8E4DC] overflow-hidden group hover:border-[#C6A85A] transition-colors duration-400">
              <div className="img-hover-zoom overflow-hidden aspect-[3/4]">
                <img
                  src={galleryImages.clothingWoman}
                  alt="Costum damă pentru ceremonii funerare – Casa Funerară Sfântul Andrei"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-[#0f0f0f]">Costum Damă</h3>
                <p className="text-xs text-[#999999] mt-1">Costum elegant pentru ceremonii</p>
              </div>
            </div>
            <div className="bg-white border border-[#E8E4DC] overflow-hidden group hover:border-[#C6A85A] transition-colors duration-400">
              <div className="img-hover-zoom overflow-hidden aspect-[3/4]">
                <img
                  src={galleryImages.clothingMan}
                  alt="Costum bărbătesc pentru ceremonii funerare – Casa Funerară Sfântul Andrei"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-[#0f0f0f]">Costum Bărbătesc</h3>
                <p className="text-xs text-[#999999] mt-1">Costum cu cravată, sobru și elegant</p>
              </div>
            </div>
            <div className="bg-white border border-[#E8E4DC] overflow-hidden group hover:border-[#C6A85A] transition-colors duration-400">
              <div className="img-hover-zoom overflow-hidden aspect-[3/4]">
                <img
                  src={galleryImages.shoesWoman}
                  alt="Pantofi damă pentru ceremonii funerare – Casa Funerară Sfântul Andrei"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-[#0f0f0f]">Pantofi Damă</h3>
                <p className="text-xs text-[#999999] mt-1">Pantofi eleganți din piele</p>
              </div>
            </div>
            <div className="bg-white border border-[#E8E4DC] overflow-hidden group hover:border-[#C6A85A] transition-colors duration-400">
              <div className="img-hover-zoom overflow-hidden aspect-[3/4]">
                <img
                  src={galleryImages.shoesMan}
                  alt="Pantofi bărbătești pentru ceremonii funerare – Casa Funerară Sfântul Andrei"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-[#0f0f0f]">Pantofi Bărbătești</h3>
                <p className="text-xs text-[#999999] mt-1">Pantofi clasici din piele</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catalog Notice */}
      <section className="py-16 bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-8 bg-[#FAFAF8] border border-[#E8E4DC]">
            <div className="flex items-start gap-4">
              <BookOpen className="w-6 h-6 text-[#999999] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-[#0f0f0f] mb-1">Catalog Complet Disponibil</h3>
                <p className="text-sm text-[#999999]">
                  Dispunem de stoc permanent și un catalog cu varietate largă de produse.
                  Contactați-ne pentru detalii și prețuri personalizate.
                </p>
              </div>
            </div>
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 bg-[#0f0f0f] text-white px-6 py-3 text-sm font-semibold no-underline hover:bg-[#1a1a1a] transition-colors whitespace-nowrap flex-shrink-0"
              style={{ borderRadius: "var(--radius-pill)" }}
            >
              <Phone className="w-4 h-4" />
              Solicită Catalog
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center relative z-10">
          <h2
            className="text-3xl sm:text-4xl font-semibold mb-6 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Contactați-ne pentru Oferte Personalizate
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mb-10">
            Vă oferim cele mai bune prețuri și pachete personalizate.
            Sunați-ne NON-STOP.
          </p>
          <a
            href={`tel:${companyInfo.phone}`}
            className="inline-flex items-center gap-3 bg-white text-[#0f0f0f] px-10 py-4 text-sm font-semibold tracking-wide no-underline hover:bg-white/90 transition-colors"
            style={{ borderRadius: "var(--radius-pill)" }}
          >
            <Phone className="w-4 h-4" />
            {companyInfo.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
